import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc, onSnapshot, getDoc } from 'firebase/firestore';
import './MakineTanımlama.css';
import { auth } from './firebaseConfig';
import { formatTrDateOnly } from '../utils/hopsDate';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X,
  Clock
} from 'lucide-react';

const MakineTanımlama = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [newMachine, setNewMachine] = useState({
    MakineAdı: '',
    BirimFiyat: '',
    MakineSaati: '',
    Marka: '',
    Model: '',
    Yıl: '',
    MakineTürü: '',
    IcSeferFiyati: '', // Kamyon için iç sefer fiyatı
    DisSeferFiyati: '' // Kamyon için dış sefer fiyatı
  });
  const [userEmail, setUserEmail] = useState('');

  const db = getFirestore();

  // Makine türleri listesi
  const makineTurleri = [
    'Asfalt Makinesi',
    'Beko Loder',
    'Belden Kırmalı Damper',
    'Beton Dağıtıcı',
    'Beton Pompası',
    'Çöp Kompaktörü',
    'Demiryolu Makinesi',
    'Dizel Çekiç',
    'Dozer',
    'Ekskavatör',
    'Finişer',
    'Fore Kazık Makinesi',
    'Greyder',
    'Kamyon', // Kamyon türü eklendi
    'Kanal & Hendek Kazıcı',
    'Kar Aracı',
    'Kar Küreme Aracı',
    'Kaya Delici',
    'Kaya Kamyonu',
    'Kırma Eleme Tesisi',
    'Kule Vinç',
    'Loder',
    'Mobil Asansör',
    'Mobil Konkasör',
    'Mobil Vinç',
    'Silindir',
    'Sondaj Makinesi',
    'Teleskopik Yükleyici',
    'Transmikser'
  ];

  // Kamyon türü kontrolü
  const isKamyonType = (type) => {
    if (!type) return false;
    const normalized = type.toLowerCase().trim();
    return normalized.includes('kamyon') || normalized.includes('truck');
  };

  // Saat değerini formatla (2 ondalık hane)
  const formatMachineSaat = (saat) => {
    if (!saat) return 'Belirtilmemiş';
    const numValue = parseFloat(saat);
    if (isNaN(numValue)) return saat;
    return numValue.toFixed(2);
  };

  // Kullanıcının email'ini al
  useEffect(() => {
    const getUserEmail = async () => {
      if (auth.currentUser?.email) {
        setUserEmail(auth.currentUser.email);
      }
    };
    getUserEmail();
  }, []);

  // Makine listesini getir (real-time)
  useEffect(() => {
    if (!userEmail) return;

    const machinesRef = collection(db, `users/${userEmail}/machines`);
    
    const unsubscribe = onSnapshot(machinesRef, (snapshot) => {
      const machineData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setMachines(machineData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userEmail, db]);

  // Yeni makine ekleme modal'ını aç
  const openAddModal = () => {
    setNewMachine({
      MakineAdı: '',
      BirimFiyat: '',
      MakineSaati: '',
      Marka: '',
      Model: '',
      Yıl: '',
      MakineTürü: '',
      IcSeferFiyati: '',
      DisSeferFiyati: ''
    });
    setShowAddModal(true);
  };

  // Yeni makine ekleme modal'ını kapat
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewMachine({
      MakineAdı: '',
      BirimFiyat: '',
      MakineSaati: '',
      Marka: '',
      Model: '',
      Yıl: '',
      MakineTürü: '',
      IcSeferFiyati: '',
      DisSeferFiyati: ''
    });
  };

  // Input değişikliklerini handle et
  const handleInputChange = (field, value) => {
    setNewMachine(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Yeni makine ekle
  const handleAddMachine = async () => {
    const isKamyon = isKamyonType(newMachine.MakineTürü);
    
    // Temel validasyon
    if (!newMachine.MakineAdı?.trim() || !newMachine.MakineSaati?.toString().trim() || !newMachine.Marka?.trim() || !newMachine.Model?.trim() || !newMachine.Yıl?.toString().trim() || !newMachine.MakineTürü?.trim()) {
      alert('Lütfen tüm alanları doldurunuz!');
      return;
    }
    
    // Kamyon için sefer fiyatları, diğerleri için birim fiyat zorunlu
    if (isKamyon) {
      if (!newMachine.IcSeferFiyati?.trim() || !newMachine.DisSeferFiyati?.trim()) {
        alert('Kamyon için iç ve dış sefer fiyatlarını giriniz!');
        return;
      }
    } else {
      if (!newMachine.BirimFiyat?.trim()) {
        alert('Birim fiyat giriniz!');
        return;
      }
    }

    try {
      const machinesRef = collection(db, `users/${userEmail}/machines`);
      
      // Kamyon için sefer fiyatlarını, diğerleri için birim fiyatı kaydet
      const machineData = {
        MakineAdı: newMachine.MakineAdı,
        MakineTürü: newMachine.MakineTürü,
        Marka: newMachine.Marka,
        Model: newMachine.Model,
        Yıl: newMachine.Yıl,
        MakineSaati: newMachine.MakineSaati,
        BirimFiyat: isKamyon ? '0' : newMachine.BirimFiyat,
        IcSeferFiyati: isKamyon ? newMachine.IcSeferFiyati : null,
        DisSeferFiyati: isKamyon ? newMachine.DisSeferFiyati : null,
        userEmail: userEmail,
        createdBy: auth.currentUser?.email,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await addDoc(machinesRef, machineData);

      // Setup progress kontrolü - En az bir makine var mı?
      const userDocRef = doc(db, 'users', userEmail);
      const updatedMachineCount = machines.length + 1;
      await updateDoc(userDocRef, {
        'setupProgress.machines': updatedMachineCount > 0
      });

      console.log('✅ Makine eklendi, setup progress güncellendi:', {
        machineCount: updatedMachineCount,
        setupCompleted: updatedMachineCount > 0
      });

      closeAddModal();
    } catch (error) {
      alert('Makine eklenirken bir hata oluştu!');
    }
  };

  // Düzenleme modal'ını aç
  const openEditModal = (machine) => {
    setEditingMachine({ 
      ...machine,
      MakineSaati: formatMachineSaat(machine.MakineSaati),
      MakineTürü: machine.MakineTürü || '',
      IcSeferFiyati: machine.IcSeferFiyati || '',
      DisSeferFiyati: machine.DisSeferFiyati || ''
    });
    setShowEditModal(true);
  };

  // Düzenleme modal'ını kapat
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingMachine(null);
  };

  // Düzenleme input değişikliklerini handle et
  const handleEditInputChange = (field, value) => {
    setEditingMachine(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Makine güncelle
  const handleUpdateMachine = async () => {
    const isKamyon = isKamyonType(editingMachine.MakineTürü);
    
    // Temel validasyon
    if (!editingMachine.MakineAdı?.trim() || !editingMachine.MakineSaati?.toString().trim() || !editingMachine.Marka?.trim() || !editingMachine.Model?.trim() || !editingMachine.Yıl?.toString().trim() || !editingMachine.MakineTürü?.trim()) {
      alert('Lütfen tüm alanları doldurunuz!');
      return;
    }
    
    // Kamyon için sefer fiyatları, diğerleri için birim fiyat zorunlu
    if (isKamyon) {
      if (!editingMachine.IcSeferFiyati?.toString().trim() || !editingMachine.DisSeferFiyati?.toString().trim()) {
        alert('Kamyon için iç ve dış sefer fiyatlarını giriniz!');
        return;
      }
    } else {
      if (!editingMachine.BirimFiyat?.toString().trim()) {
        alert('Birim fiyat giriniz!');
        return;
      }
    }

    try {
      const machineDocRef = doc(db, `users/${userEmail}/machines`, editingMachine.id);
      
      const updateData = {
        MakineAdı: editingMachine.MakineAdı,
        BirimFiyat: isKamyon ? '0' : editingMachine.BirimFiyat,
        MakineSaati: editingMachine.MakineSaati,
        Marka: editingMachine.Marka,
        Model: editingMachine.Model,
        Yıl: editingMachine.Yıl,
        MakineTürü: editingMachine.MakineTürü,
        IcSeferFiyati: isKamyon ? editingMachine.IcSeferFiyati : null,
        DisSeferFiyati: isKamyon ? editingMachine.DisSeferFiyati : null,
        updatedBy: auth.currentUser?.email,
        updatedAt: new Date().toISOString()
      };
      
      console.log('🚛 Makine güncelleniyor:', updateData);
      
      await updateDoc(machineDocRef, updateData);

      closeEditModal();
    } catch (error) {
      console.error('❌ Makine güncelleme hatası:', error);
      alert('Makine güncellenirken bir hata oluştu!');
    }
  };

  // Makine sil
  const handleDeleteMachine = async (machineId) => {
    if (!window.confirm('Bu makineyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const machineDocRef = doc(db, `users/${userEmail}/machines`, machineId);
      await deleteDoc(machineDocRef);

      // Setup progress kontrolü - Makine silindikten sonra kontrol et
      const userDocRef = doc(db, 'users', userEmail);
      const updatedMachineCount = machines.length - 1;
      await updateDoc(userDocRef, {
        'setupProgress.machines': updatedMachineCount > 0
      });

      console.log('✅ Makine silindi, setup progress güncellendi:', {
        machineCount: updatedMachineCount,
        setupCompleted: updatedMachineCount > 0
      });
    } catch (error) {
      alert('Makine silinirken bir hata oluştu!');
    }
  };

  if (loading) {
    return (
      <div className="makine-tanitim-loading">
        <div className="loading-spinner"></div>
        <p>Makineler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="makine-tanitim-container">
      <div className="makine-tanitim-header">
        <h2>
          <Settings className="header-icon" />
          Makine Tanımlama
        </h2>
        <button 
          className="add-makine-btn"
          onClick={openAddModal}
        >
          <Plus size={16} />
          Yeni Makine Ekle
        </button>
      </div>

      <div className="makine-list-container">
        {machines.length === 0 ? (
          <div className="no-makine">
            <Settings size={48} />
            <h3>Henüz makine eklenmemiş</h3>
            <p>İlk makinenizi eklemek için yukarıdaki butona tıklayın.</p>
          </div>
        ) : (
          <div className="makine-grid">
            {machines.map((machine) => (
              <div key={machine.id} className="makine-card">
                <div className="makine-card-header">
                  <div className="makine-info">
                    <h3>{machine.MakineAdı}</h3>
                  </div>
                  <div className="makine-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => openEditModal(machine)}
                      title="Düzenle"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteMachine(machine.id)}
                      title="Sil"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="makine-details">
                  <div className="detail-item">
                    Makine Türü: {machine.MakineTürü || 'Belirtilmemiş'}
                  </div>
                  <div className="detail-item">
                    Marka: {machine.Marka || 'Belirtilmemiş'}
                  </div>
                  <div className="detail-item">
                    Model: {machine.Model || 'Belirtilmemiş'}
                  </div>
                  <div className="detail-item">
                    Yıl: {machine.Yıl || 'Belirtilmemiş'}
                  </div>
                  <div className="detail-item">
                    <Clock size={14} style={{marginRight: '6px'}} />
                    Saat: {formatMachineSaat(machine.MakineSaati)}
                  </div>
                  {/* Kamyon için sefer fiyatları, diğerleri için birim fiyat */}
                  {isKamyonType(machine.MakineTürü) ? (
                    <>
                      <div className="detail-item">
                        İç Sefer Fiyatı: {machine.IcSeferFiyati || '0'} ₺/sefer
                      </div>
                      <div className="detail-item">
                        Dış Sefer Fiyatı: {machine.DisSeferFiyati || '0'} ₺/sefer
                      </div>
                    </>
                  ) : (
                    <div className="detail-item">
                      Birim Fiyat: {machine.BirimFiyat} ₺
                    </div>
                  )}
                  <div className="detail-item">
                    Eklenme Tarihi: {machine.createdAt ? formatTrDateOnly(machine.createdAt) : 'Bilinmiyor'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Yeni Makine Ekleme Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Yeni Makine Ekle</h3>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Kodu:</span>
                    <input
                      type="text"
                      value={newMachine.MakineAdı}
                      onChange={(e) => handleInputChange('MakineAdı', e.target.value)}
                      placeholder="EXC-001"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Türü:</span>
                    <select
                      value={newMachine.MakineTürü}
                      onChange={(e) => handleInputChange('MakineTürü', e.target.value)}
                      className="form-input inline"
                    >
                      <option value="">Makine türü seçin...</option>
                      {makineTurleri.map((tur, index) => (
                        <option key={index} value={tur}>
                          {tur}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Marka:</span>
                    <input
                      type="text"
                      value={newMachine.Marka}
                      onChange={(e) => handleInputChange('Marka', e.target.value)}
                      placeholder="Manitou"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Model:</span>
                    <input
                      type="text"
                      value={newMachine.Model}
                      onChange={(e) => handleInputChange('Model', e.target.value)}
                      placeholder="MTX 18-40"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Yıl:</span>
                    <input
                      type="number"
                      value={newMachine.Yıl}
                      onChange={(e) => handleInputChange('Yıl', e.target.value)}
                      placeholder="2020"
                      min="1990"
                      max="2030"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Saat:</span>
                    <input
                      type="number"
                      step="0.01"
                      value={newMachine.MakineSaati}
                      onChange={(e) => handleInputChange('MakineSaati', e.target.value)}
                      placeholder="5463.50"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              {/* Kamyon için sefer fiyatları, diğerleri için birim fiyat */}
              {isKamyonType(newMachine.MakineTürü) ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <div className="inline-input">
                        <span className="input-label">İç Sefer Fiyatı (₺/sefer):</span>
                        <input
                          type="number"
                          value={newMachine.IcSeferFiyati}
                          onChange={(e) => handleInputChange('IcSeferFiyati', e.target.value)}
                          placeholder="500"
                          className="form-input inline"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <div className="inline-input">
                        <span className="input-label">Dış Sefer Fiyatı (₺/sefer):</span>
                        <input
                          type="number"
                          value={newMachine.DisSeferFiyati}
                          onChange={(e) => handleInputChange('DisSeferFiyati', e.target.value)}
                          placeholder="800"
                          className="form-input inline"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <p style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                      💡 Kamyon için sefer bazlı fiyatlandırma kullanılır. Puantaj girişinde sefer sayısı seçilecektir.
                    </p>
                  </div>
                </>
              ) : (
                <div className="form-row">
                  <div className="form-group">
                    <div className="inline-input">
                      <span className="input-label">Birim Fiyat:</span>
                      <input
                        type="text"
                        value={newMachine.BirimFiyat}
                        onChange={(e) => handleInputChange('BirimFiyat', e.target.value)}
                        placeholder="3000"
                        className="form-input inline"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeAddModal}>
                İptal
              </button>
              <button className="save-btn" onClick={handleAddMachine}>
                <Save size={16} />
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Makine Düzenleme Modal */}
      {showEditModal && editingMachine && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Makine Düzenle</h3>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Kodu:</span>
                    <input
                      type="text"
                      value={editingMachine.MakineAdı}
                      onChange={(e) => handleEditInputChange('MakineAdı', e.target.value)}
                      placeholder="EXC-001"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Türü:</span>
                    <select
                      value={editingMachine.MakineTürü || ''}
                      onChange={(e) => handleEditInputChange('MakineTürü', e.target.value)}
                      className="form-input inline"
                    >
                      <option value="">Makine türü seçin...</option>
                      {makineTurleri.map((tur, index) => (
                        <option key={index} value={tur}>
                          {tur}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Marka:</span>
                    <input
                      type="text"
                      value={editingMachine.Marka || ''}
                      onChange={(e) => handleEditInputChange('Marka', e.target.value)}
                      placeholder="Manitou"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Model:</span>
                    <input
                      type="text"
                      value={editingMachine.Model || ''}
                      onChange={(e) => handleEditInputChange('Model', e.target.value)}
                      placeholder="MTX 18-40"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Makine Yıl:</span>
                    <input
                      type="number"
                      value={editingMachine.Yıl || ''}
                      onChange={(e) => handleEditInputChange('Yıl', e.target.value)}
                      placeholder="2020"
                      min="1990"
                      max="2030"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <div className="inline-input">
                    <span className="input-label">Saat:</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editingMachine.MakineSaati}
                      onChange={(e) => handleEditInputChange('MakineSaati', e.target.value)}
                      placeholder="5463.50"
                      className="form-input inline"
                    />
                  </div>
                </div>
              </div>
              
              {/* Kamyon için sefer fiyatları, diğerleri için birim fiyat */}
              {isKamyonType(editingMachine.MakineTürü) ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <div className="inline-input">
                        <span className="input-label">İç Sefer Fiyatı (₺/sefer):</span>
                        <input
                          type="number"
                          value={editingMachine.IcSeferFiyati || ''}
                          onChange={(e) => handleEditInputChange('IcSeferFiyati', e.target.value)}
                          placeholder="500"
                          className="form-input inline"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <div className="inline-input">
                        <span className="input-label">Dış Sefer Fiyatı (₺/sefer):</span>
                        <input
                          type="number"
                          value={editingMachine.DisSeferFiyati || ''}
                          onChange={(e) => handleEditInputChange('DisSeferFiyati', e.target.value)}
                          placeholder="800"
                          className="form-input inline"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <p style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                      💡 Kamyon için sefer bazlı fiyatlandırma kullanılır. Puantaj girişinde sefer sayısı seçilecektir.
                    </p>
                  </div>
                </>
              ) : (
                <div className="form-row">
                  <div className="form-group">
                    <div className="inline-input">
                      <span className="input-label">Birim Fiyat:</span>
                      <input
                        type="text"
                        value={editingMachine.BirimFiyat}
                        onChange={(e) => handleEditInputChange('BirimFiyat', e.target.value)}
                        placeholder="3000"
                        className="form-input inline"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeEditModal}>
                İptal
              </button>
              <button className="save-btn" onClick={handleUpdateMachine}>
                <Save size={16} />
                Güncelle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MakineTanımlama;
